import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FeatureUsageOrigin } from '@nimbus-library/utils/feature-usage-origin';
import { IUserTasksCapacity } from '@nimbus-global/features/user-tasks-capacity/models/user-tasks-capacity.model';
import { UserTasksCapacityService } from '@nimbus-global/features/user-tasks-capacity/services/user-tasks-capacity.service';
import { UserTpCapacityComponent } from '@nimbus-global/features/user-tasks-capacity/components/user-tp-capacity/user-tp-capacity.component';
import { UserTpCapacityButtonComponent } from '@nimbus-global/features/user-tasks-capacity/components/user-tp-capacity-button/user-tp-capacity-button.component';
import { UserHeaderStateService } from '@nimbus-portal/features/user-header/services/user-header-state.service';
import { HeaderBarPresenceComponent } from './components/header-bar-presence/header-bar-presence.component';
import { HeaderBarServicesComponent } from './components/header-bar-services/header-bar-services.component';
import { HeaderBarQuickActionsComponent } from './components/header-bar-quick-actions/header-bar-quick-actions.component';
import { HeaderBarHelpComponent } from './components/header-bar-help/header-bar-help.component';
import { HeaderBarPinnedServicesComponent } from './components/header-bar-pinned-services/header-bar-pinned-services.component';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-header-bar',
  templateUrl: './header-bar.component.html',
  styleUrl: './header-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    HeaderBarPresenceComponent,
    HeaderBarServicesComponent,
    HeaderBarQuickActionsComponent,
    HeaderBarHelpComponent,
    HeaderBarPinnedServicesComponent,
    UserTpCapacityComponent,
    UserTpCapacityButtonComponent,
  ],
})
export class HeaderBarComponent implements OnInit, OnDestroy {
  // Below this header-bar width the full inline capacity is replaced by the compact button.
  private static readonly COMPACT_WIDTH_PX = 1100;

  private readonly width$ = new BehaviorSubject<number>(Number.MAX_SAFE_INTEGER);
  private resizeObserver: ResizeObserver | null = null;

  public readonly origin = FeatureUsageOrigin.PortalHeaderBar;

  public capacity$: Observable<IUserTasksCapacity | null>;
  public readonly isCapacityCompact$: Observable<boolean>;

  public constructor(
    private readonly userTasksCapacityService: UserTasksCapacityService,
    private readonly userHeaderStateService: UserHeaderStateService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
  ) {
    this.isCapacityCompact$ = this.width$.pipe(
      map((width) => width < HeaderBarComponent.COMPACT_WIDTH_PX),
      distinctUntilChanged(),
    );
  }

  public ngOnInit(): void {
    this.capacity$ = this.userTasksCapacityService.capacity$;
    this.userHeaderStateService.initialize();
    this.refreshMembershipsOnNavigation();
    this.observeWidth();
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private refreshMembershipsOnNavigation(): void {
    // The persistent header is no longer recreated when moving between pages (e.g. Overview <-> My
    // Sessions), so refresh memberships on navigation to pick up teams added/removed server-side;
    // the live event handler only toggles `enabled` on already-known memberships.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => this.userHeaderStateService.initialize());
  }

  private observeWidth(): void {
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        this.ngZone.run(() => {
          this.width$.next(width);
          this.cdr.markForCheck();
        });
      });
      this.resizeObserver.observe(this.elementRef.nativeElement);
    });
  }
}
